import { isSuppressed } from '@/types/domain'
import type { CaseLeg } from '@/types/domain'

/** Whether this caller was refused the direction's commercial identity (AD-6).
 *
 *  Read off the PAYLOAD, never re-derived from a role this client happens to
 *  know: the leg's carrier and each truck's carrier and driver are gated by one
 *  server-side decision (`toVehicleDTOs`), so one hidden field means the whole
 *  group was withheld from this reader.
 *
 *  WHAT IT COSTS IS THE SAVE, and that is the point of having it in one place.
 *  Every write behind a leg form is a whole-record PUT: a caller who cannot read
 *  the haulier submits the fields blank, and the server reads a blank as "nobody
 *  states this" and clears the carrier of the direction and of every truck on
 *  it. There is no "leave it alone" on that wire, so the honest control is a
 *  refusal with the reason on it — `cases.legs.identityHidden` — offered by
 *  every surface that can submit one.
 *
 *  It is not a permission check. The server refuses the caller regardless
 *  (AD-21); this stops a well-meaning save from destroying what it cannot see. */
export function legIdentityHidden(leg: CaseLeg): boolean {
  return (
    isSuppressed(leg.carrierName) ||
    isSuppressed(leg.carrierId) ||
    (leg.vehicles ?? []).some(
      (vehicle) =>
        isSuppressed(vehicle.carrierName) ||
        isSuppressed(vehicle.carrierId) ||
        isSuppressed(vehicle.driverName) ||
        isSuppressed(vehicle.driverPhone),
    )
  )
}
